/**
 * UTM attribution capture.
 *
 * On first visit, snapshot any `utm_*` params from the URL into
 * localStorage so the source survives bounce/return/signup flows.
 * The captured payload is later attached to signup + offer + order
 * requests so Phase 2 analytics can attribute revenue back to Reddit
 * (and other) campaigns.
 *
 * Capture rules:
 * - First touch wins. We never overwrite a stored attribution unless
 *   the caller explicitly passes `overwrite: true`. This matches the
 *   "Reddit visitor signs up two weeks later from a different tab"
 *   case — the original Reddit touch should still get credit.
 * - Expires after 30 days. Long enough for delayed conversion,
 *   short enough that stale attribution doesn't mislead reporting.
 */
const STORAGE_KEY = 'oj_utm_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
] as const;

export interface UtmAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  ref?: string;
  landingPath: string;
  capturedAt: number;
}

function readStored(): UtmAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UtmAttribution;
    if (Date.now() - parsed.capturedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function captureUtmOnLoad(opts: { overwrite?: boolean } = {}): UtmAttribution | null {
  if (typeof window === 'undefined') return null;

  const existing = readStored();
  if (existing && !opts.overwrite) return existing;

  const params = new URLSearchParams(window.location.search);
  const present = UTM_KEYS.some((k) => params.has(k));
  if (!present) return existing;

  const payload: UtmAttribution = {
    source: params.get('utm_source') ?? undefined,
    medium: params.get('utm_medium') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
    term: params.get('utm_term') ?? undefined,
    content: params.get('utm_content') ?? undefined,
    ref: params.get('ref') ?? undefined,
    landingPath: window.location.pathname,
    capturedAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode etc — first-party storage may be blocked */
  }

  return payload;
}

export function getStoredUtm(): UtmAttribution | null {
  if (typeof window === 'undefined') return null;
  return readStored();
}

export function clearStoredUtm(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
